import React, { ComponentProps, ReactNode, useCallback, useMemo } from "react";

// Hook Form
import {
  useController,
  FieldPath,
  FieldValues,
  Control,
  RegisterOptions,
} from "react-hook-form";

// MUI
import {
  TextField,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Autocomplete,
  CircularProgress,
  AutocompleteProps,
  Box,
  OutlinedInputProps,
  InputProps,
  FilledInputProps,
  InputBaseProps,
  Stack,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

// Serve per normalizzare i valori in stringa
const normalizeToString = (value: any) =>
  value === null || value === undefined ? "" : String(value);

//Funzione per controllare l'uguaglianza di due oggetti tramite le chiavi indicate
//Devi passare un array di chiavi da confrontare es. ["codice", "descrizione"]
export const createIsOptionEqualByKeys =
  (compareKeys: string[]) =>
  (option: any, value: any): boolean => {
    if (!option || !value) return false;

    const valueAsObject =
      typeof value === "object" ? value : { __rawValue: value };

    for (const key of compareKeys) {
      const optionValue = normalizeToString(option?.[key]);

      const formValue =
        key === "__rawValue"
          ? normalizeToString(valueAsObject.__rawValue)
          : normalizeToString(valueAsObject?.[key] ?? valueAsObject.__rawValue);

      if (optionValue && formValue && optionValue === formValue) {
        return true;
      }
    }

    return false;
  };

// — utils per equality e per includere il value tra le options —
const byKey = (key: string) => (a: any, b: any) =>
  !!a && !!b && String(a?.[key] ?? "") === String(b?.[key] ?? "");

//Funzione per normalizzare gli orari ed evitare che restituiscano doppi : o formati incongruenti
/**
 * Normalizza un orario proveniente dalle API in formato "HH:mm"
 *
 * Le API Medint possono restituire orari in formati diversi:
 *  - "1"        -> 01:00
 *  - "10"       -> 10:00
 *  - "900"      -> 09:00
 *  - "2359"     -> 23:59
 *  - "12:30"    -> già ok
 *  - "12:30:45" -> prendo solo ore e minuti
 *
 * Questa funzione converte tutto in "HH:mm" per poterlo usare
 * in modo sicuro con MUI TimePicker e React Hook Form.
 */
export const normalizeHour = (v?: string | null) => {
  // Se non c'è valore, restituisco null (campo vuoto)
  if (!v) return null;

  // Caso: formato già corretto "HH:mm" → lo uso così com'è
  if (/^\d{2}:\d{2}$/.test(v)) return v;

  // Caso: formato con secondi "HH:mm:ss" → taglio i secondi
  if (/^\d{2}:\d{2}:\d{2}$/.test(v)) return v.slice(0, 5);

  // Caso: solo numeri (formati legacy dell'API: H, HH, HMM, HHMM, ecc.)
  if (/^\d+$/.test(v)) {
    const s = v.trim();

    // 1 o 2 cifre → solo ore, minuti impliciti = 00
    // "1"  -> "01:00"
    // "10" -> "10:00"
    if (s.length <= 2) {
      return `${s.padStart(2, "0")}:00`;
    }

    // 3 cifre → formato HMM
    // "900" -> "09:00"
    if (s.length === 3) {
      return `${s[0].padStart(2, "0")}:${s.slice(1)}`;
    }

    // 4 cifre → formato HHMM
    // "2359" -> "23:59"
    if (s.length === 4) {
      return `${s.slice(0, 2)}:${s.slice(2)}`;
    }

    // Caso strano: stringhe numeriche più lunghe (es. timestamp, errori backend, ecc.)
    // Prendo comunque le ultime 4 cifre come HHMM
    const last4 = s.slice(-4);
    return `${last4.slice(0, 2)}:${last4.slice(2)}`;
  }

  // Fallback: se arriva qualcosa di inatteso, lo restituisco così com'è
  // (lato ui lo filtriamo e mostriamo corretto attraverso gli rhf)
  return v;
};

function ensureInOptions<T>(
  options: T[] | undefined | null,
  value: T | T[] | null | undefined,
  eq: (a: T, b: T) => boolean,
): T[] {
  const opts = options ?? [];
  if (!value) return opts;

  if (Array.isArray(value)) {
    const missing = value.filter((v) => !opts.some((o) => eq(o, v)));
    return missing.length ? [...opts, ...missing] : opts;
  }
  return opts.some((o) => eq(o, value)) ? opts : [...opts, value];
}

/* -------------------------------------------------------------------------------------------------
 * RHFTextField
 * Wrapper per TextField di MUI collegato a RHF
 * - Accetta name + control dal form
 * - Gestisce value, onChange, onBlur, ref in automatico
 * - Mostra errori e helperText se presenti
 * ------------------------------------------------------------------------------------------------- */

export type RHFTextFieldProps<TValues extends FieldValues> = {
  control: Control<TValues, any>;
  name: FieldPath<TValues>;
  //Sono le regole di validazione come ad esempio required, minLength, validate ecc.
  rules?: RegisterOptions<TValues, FieldPath<TValues>>;
  //Recupero tutte le props che gestisce la Textfield e con Omit tolgo quelle che gestisco con rhf per evitare conflitti
} & Omit<
  ComponentProps<typeof TextField>,
  "name" | "value" | "onChange" | "defaultValue" | "inputRef"
>;

export function RHFTextField<TValues extends FieldValues>({
  control,
  name,
  rules,
  ...props
}: RHFTextFieldProps<TValues>) {
  const {
    field: { value, onChange, onBlur, ref }, // binding a RHF
    fieldState: { error }, // stato di validazione
  } = useController<TValues>({ name, control, rules });

  return (
    <TextField
      size="small"
      inputRef={ref}
      value={value ?? ""} // evita warning uncontrolled/controlled
      onChange={onChange}
      onBlur={onBlur}
      error={!!error}
      helperText={error?.message} // messaggio di errore da schema
      {...props}
    />
  );
}

/* -------------------------------------------------------------------------------------------------
 * RHFCheckbox
 * Wrapper per Checkbox di MUI
 * - Converte automaticamente boolean <-> checked
 * - Accetta anche un label opzionale
 * ------------------------------------------------------------------------------------------------- */

export type RHFCheckboxProps<TValues extends FieldValues> = {
  control: Control<TValues, any>;
  name: FieldPath<TValues>;
  //ReactNode rappresenta qualsiasi cosa renderizzabile da React, come stringhe, elementi tsx ecc
  label?: ReactNode;
  rules?: RegisterOptions<TValues, FieldPath<TValues>>;
} & Omit<
  ComponentProps<typeof Checkbox>,
  "name" | "checked" | "onChange" | "defaultChecked" | "inputRef"
>;

export function RHFCheckbox<TValues extends FieldValues>({
  control,
  name,
  label,
  rules,
  ...props
}: RHFCheckboxProps<TValues>) {
  const {
    field: { value, onChange, ref },
    fieldState: { error },
  } = useController<TValues>({ name, control, rules });

  return (
    <FormControl error={!!error}>
      <FormControlLabel
        control={
          <Checkbox
            inputRef={ref}
            checked={!!value} // forza boolean
            onChange={(e) => onChange(e.target.checked)}
            {...props}
          />
        }
        label={label}
      />
    </FormControl>
  );
}

/* -------------------------------------------------------------------------------------------------
 * RHFRadioGroup
 * Wrapper per gruppo di radio button
 * - Usa un array di opzioni {value, label}
 * - Gestisce value e validazione
 * ------------------------------------------------------------------------------------------------- */

export type RHFRadioOption = { label: ReactNode; value: string | number };

export type RHFRadioGroupProps<TValues extends FieldValues> = {
  control: Control<TValues, any>;
  name: FieldPath<TValues>;
  label?: ReactNode;
  options: RHFRadioOption[];
  rules?: RegisterOptions<TValues, FieldPath<TValues>>;
} & Omit<
  ComponentProps<typeof RadioGroup>,
  "name" | "value" | "onChange" | "ref"
>;

export function RHFRadioGroup<TValues extends FieldValues>({
  control,
  name,
  label,
  options,
  rules,
  ...props
}: RHFRadioGroupProps<TValues>) {
  const {
    field: { value, onChange, ref },
    fieldState: { error },
  } = useController<TValues>({ name, control, rules });

  return (
    <FormControl error={!!error} fullWidth>
      {label && <FormLabel>{label}</FormLabel>}
      <RadioGroup
        ref={ref}
        value={value ?? ""} // default vuoto
        onChange={(e) => onChange(e.target.value)}
        {...props}
      >
        {options.map((opt) => (
          <FormControlLabel
            key={String(opt.value)}
            value={opt.value}
            control={<Radio />}
            label={opt.label}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
}

/* -------------------------------------------------------------------------------------------------
 * RHFAutocomplete
 * Wrapper per Autocomplete (singolo o multiplo)
 * - mapIn:    valore del form -> valore da mostrare (oggetto/i)
   - mapOut:   selezione UI -> valore da salvare (id/i o oggetto/i)
 * - isOptionEqualToValue: come capire se due opzioni sono uguali (es. per id)
 * - Gestisce anche loading (spinner integrato)
 * - Supporta input controllato (utile per search remota con debounce)
 * - getOptionLabel: come mostrare il testo di ogni opzione
 * ------------------------------------------------------------------------------------------------- */

// Props di base di Autocomplete (tolte quelle che gestiamo noi)
type BaseAutocompleteProps<TOption> = Omit<
  AutocompleteProps<TOption, boolean, false, false>,
  | "options"
  | "multiple"
  | "value"
  | "onChange"
  | "renderInput"
  | "isOptionEqualToValue"
>;

export type RHFAutocompleteProps<
  TValues extends FieldValues = any,
  TOption = any,
  TFormValue = TOption | TOption[] | null,
> = {
  control: Control<TValues, any>;
  name: FieldPath<TValues>;
  placeholder?: string;
  options?: TOption[];
  multiple?: boolean;
  label?: ReactNode;
  loading?: boolean;
  rules?: RegisterOptions<TValues, FieldPath<TValues>>;
  getOptionLabel?: (opt: TOption) => string;
  isOptionEqualToValue?: (opt: TOption, val: TOption) => boolean;
  mapIn?: (formValue: any, options: TOption[]) => TOption | TOption[] | null;
  mapOut?: (opt: TOption | TOption[] | null) => TFormValue;
  inputValue?: string;
  onInputValueChange?: (next: string) => void;
  keyField?: string;
  autoIncludeValue?: boolean;
  getOptionKey?: (opt: TOption, index: number) => string;
  customInputProps?: Partial<InputBaseProps>;
  onValueChange?: (next: TFormValue) => void;
} & BaseAutocompleteProps<TOption>;

export function RHFAutocomplete<
  TValues extends FieldValues = any,
  TOption = any,
  TFormValue = TOption | TOption[] | null,
>({
  control,
  name,
  placeholder,
  options,
  multiple = false,
  label,
  loading,
  getOptionLabel,
  isOptionEqualToValue,
  mapIn,
  mapOut,
  inputValue,
  onInputValueChange,
  rules,
  keyField,
  autoIncludeValue = true,
  getOptionKey,
  customInputProps,
  onValueChange,
  ...props
}: RHFAutocompleteProps<TValues, TOption, TFormValue>) {
  const {
    field: { value: rawValue, onChange, ref, onBlur },
    fieldState: { error },
  } = useController<TValues>({ name, control, rules });

  // default: nessuna opzione → lista vuota
  const opts = options ?? [];

  // fallback label: stringa → sé stessa; oggetto → label se c’è, altrimenti toString
  const getLabel =
    getOptionLabel ??
    ((o: any) => (typeof o === "string" ? o : (o?.label ?? String(o))));

  // fallback equality: confronta id se presenti, altrimenti il valore grezzo
  const eq =
    (keyField ? (byKey(keyField) as any) : isOptionEqualToValue) ??
    ((a: any, b: any) => (a?.id ?? a) === (b?.id ?? b));

  const viewValue = useMemo(() => {
    if (mapIn) return mapIn(rawValue, opts);
    return multiple ? (rawValue ?? []) : (rawValue ?? null);
  }, [mapIn, rawValue, opts, multiple]);

  const safeOptions = useMemo(
    () =>
      autoIncludeValue ? ensureInOptions(opts, viewValue as any, eq) : opts,
    [opts, viewValue, eq, autoIncludeValue],
  );

  const handleChange = useCallback(
    (_: unknown, newValue: TOption | TOption[] | null) => {
      const nextFormValue = (
        mapOut ? mapOut(newValue) : (newValue as any)
      ) as TFormValue;

      onChange(nextFormValue); // aggiorna RHF
      onValueChange?.(nextFormValue);
    },
    [onChange, mapOut, onValueChange],
  );

  // default per la key se non viene passato getOptionKey
  const fallbackGetKey = useCallback(
    (o: any, idx: number) => {
      const base = String(
        (keyField ? o?.[keyField] : undefined) ??
          o?.id ??
          o?.ID ??
          o?.codice ??
          o?.PRESTAZIONE ??
          getLabel(o),
      );

      // sempre suffisso indice => unicità garantita anche se base è duplicata
      return `${base}__${idx}`;
    },
    [getLabel, keyField],
  );

  return (
    <Autocomplete<TOption, boolean, false, false>
      renderOption={(liProps, option, state) => {
        if (getOptionKey) {
          const { key, ...rest } = liProps;
          const forcedKey = getOptionKey(option, state.index);
          return (
            <li {...rest} key={forcedKey}>
              {getLabel(option)}
            </li>
          );
        }

        const { key, ...rest } = liProps;
        const safeKey = fallbackGetKey(option as any, state.index);

        return (
          <li {...rest} key={safeKey}>
            {getLabel(option)}
          </li>
        );
      }}
      multiple={multiple}
      size="small"
      options={safeOptions}
      value={viewValue as any}
      onChange={handleChange}
      isOptionEqualToValue={eq}
      getOptionLabel={getLabel as any}
      onBlur={onBlur}
      inputValue={inputValue}
      onInputChange={(_, v) => onInputValueChange?.(v)}
      renderInput={(params, ...cust) => (
        <TextField
          {...params}
          inputRef={ref}
          label={label}
          error={!!error}
          helperText={error?.message}
          InputProps={{
            ...params.InputProps,
            ...customInputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                <Stack direction={"row"} gap={2}>
                  {customInputProps?.endAdornment}
                  {params.InputProps.endAdornment}
                </Stack>
              </>
            ),
          }}
        />
      )}
      {...props}
    />
  );
}

/* ================================================================================================
 * RHFDateTimePicker (singolo) — usa DatePicker se dateOnly=true, altrimenti DateTimePicker
 * ================================================================================================ */
type RHFDateTimePickerProps<TValues extends FieldValues> = {
  control: Control<TValues, any>;
  name: FieldPath<TValues>;
  rules?: RegisterOptions<TValues, FieldPath<TValues>>;
  label?: ReactNode;
  fullWidth?: boolean;

  dateOnly?: boolean;
  ampm?: boolean;

  /** come salvo nel form */
  serialize?: "string" | "dayjs";

  /** formato mostrato in UI (input del picker) */
  displayFormat?: string;

  /** formato salvato nel form quando serialize="string" */
  valueFormat?: string;

  size?: "small" | "medium";
  disabled?: boolean;

  minValue?: Dayjs | string | null;
  maxValue?: Dayjs | string | null;

  minutesStep?: number;
};

export function RHFDateTimePicker<TValues extends FieldValues>({
  control,
  name,
  rules,
  label,
  dateOnly = false,
  ampm = false,
  serialize = "string",
  displayFormat,
  valueFormat,
  size = "small",
  disabled,
  minValue,
  maxValue,
  minutesStep = 1,
  fullWidth = false,
}: RHFDateTimePickerProps<TValues>) {
  const {
    field,
    fieldState: { error },
  } = useController<TValues>({ name, control, rules });

  // UI: anno corto
  const fmtDisplay =
    displayFormat ?? (dateOnly ? "DD/MM/YY" : "DD/MM/YY HH:mm");

  // Storage: anno intero
  const fmtValue =
    valueFormat ?? (dateOnly ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm");

  const viewValue = useMemo<Dayjs | null>(() => {
    const v = field.value as unknown;

    if (serialize === "dayjs") {
      if (!v) return null;
      const d = v as Dayjs;
      return d.format("YYYY-MM-DD") === "9999-12-31" ? null : d;
    }

    if (typeof v === "string") {
      if (!v) return null;

      if (v.startsWith("9999-12-31")) return null;

      const parsed = dayjs(v, fmtValue, true);
      if (parsed.isValid()) return parsed;

      const iso = dayjs(v);
      return iso.isValid() ? iso : null;
    }

    return null;
  }, [field.value, serialize, fmtValue]);

  const toDayjs = (v?: Dayjs | string | null) => {
    if (!v) return null;
    if (typeof v === "string") {
      const p = dayjs(v, fmtValue, true);
      return p.isValid() ? p : dayjs(v);
    }
    return v;
  };

  const min = toDayjs(minValue) ?? undefined;
  const max = toDayjs(maxValue) ?? undefined;

  const handleChange = useCallback(
    (d: Dayjs | null) => {
      if (serialize === "dayjs") field.onChange(d);
      //Qui salvo l'anno per intero
      else field.onChange(d ? d.format(fmtValue) : null);
    },
    [field, serialize, fmtValue],
  );

  if (dateOnly) {
    return (
      <DatePicker
        label={label as any}
        format={fmtDisplay}
        value={viewValue ?? null}
        onChange={handleChange}
        disabled={disabled}
        minDate={min}
        maxDate={max}
        slotProps={{
          textField: {
            size,
            error: !!error,
            helperText: error?.message,
            fullWidth,
          },
        }}
      />
    );
  }

  return (
    <DateTimePicker
      label={label as any}
      value={viewValue ?? null}
      onChange={handleChange}
      ampm={ampm}
      format={fmtDisplay} // ✅ mostro anno corto
      disabled={disabled}
      minutesStep={minutesStep}
      minDateTime={min}
      maxDateTime={max}
      slotProps={{
        textField: {
          size,
          error: !!error,
          helperText: error?.message,
          fullWidth,
        },
      }}
    />
  );
}

/* ================================================================================================
 * RHFDateTimeRangePicker; con dateOnly=true diventa solo calendario
 * ================================================================================================ */

type StringRange = [string | null, string | null];

type RHFDateTimeRangePickerProps<TValues extends FieldValues> = {
  control: Control<TValues, any>;
  // FieldPath<TValues> è una stringa tipizzata che può essere solo una chiave valida del form.
  //In questo modo si evitano i refusi
  name: FieldPath<TValues>;
  rules?: RegisterOptions<TValues, FieldPath<TValues>>;
  startLabel?: ReactNode;
  endLabel?: ReactNode;

  dateOnly?: boolean;
  ampm?: boolean;

  serialize?: "string" | "dayjs";
  format?: string;
  size?: "small" | "medium";
  disabled?: boolean;
  minutesStep?: number;
  fullWidth?: boolean;
};

export function RHFDateTimeRangePicker<TValues extends FieldValues>({
  control,
  name,
  rules,
  startLabel = "Dal",
  endLabel = "Al",
  dateOnly = false,
  ampm = false,
  serialize = "string",
  format,
  size = "small",
  disabled,
  minutesStep = 1,
  fullWidth = false,
}: RHFDateTimeRangePickerProps<TValues>) {
  const {
    field,
    fieldState: { error },
  } = useController<TValues>({ name, control, rules });

  const fmt = format ?? (dateOnly ? "DD/MM/YY" : "DD/MM/YY HH:mm");

  const [start, end] = useMemo<[Dayjs | null, Dayjs | null]>(() => {
    const v = (field.value as [any, any]) ?? [null, null];
    if (serialize === "dayjs") return [v?.[0] ?? null, v?.[1] ?? null];
    return [
      v?.[0] ? dayjs(v[0], fmt, true) : null,
      v?.[1] ? dayjs(v[1], fmt, true) : null,
    ];
  }, [field.value, fmt, serialize]);

  const setStart = useCallback(
    (d: Dayjs | null) => {
      if (serialize === "dayjs") field.onChange([d, end]);
      else {
        const next: StringRange = [
          d ? d.format(fmt) : null,
          end ? end.format(fmt) : null,
        ];
        field.onChange(next);
      }
    },
    [end, field, serialize, fmt],
  );

  const setEnd = useCallback(
    (d: Dayjs | null) => {
      if (serialize === "dayjs") field.onChange([start, d]);
      else {
        const next: StringRange = [
          start ? start.format(fmt) : null,
          d ? d.format(fmt) : null,
        ];
        field.onChange(next);
      }
    },
    [start, field, serialize, fmt],
  );

  return (
    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
      {dateOnly ? (
        <>
          <DatePicker
            label={startLabel as any}
            value={start}
            onChange={setStart}
            disabled={disabled}
            format={fmt}
            slotProps={{
              textField: {
                size,
                error: !!error,
                helperText: error?.message,
                fullWidth,
              },
            }}
          />
          <DatePicker
            label={endLabel as any}
            value={end}
            onChange={setEnd}
            format={fmt}
            disabled={disabled}
            minDate={start ?? undefined}
            slotProps={{
              textField: {
                size,
                error: !!error,
                helperText: error?.message,
                fullWidth,
              },
            }}
          />
        </>
      ) : (
        <>
          <DateTimePicker
            label={startLabel as any}
            value={start}
            onChange={setStart}
            format={fmt}
            ampm={ampm}
            disabled={disabled}
            minutesStep={minutesStep}
            slotProps={{
              textField: {
                size,
                error: !!error,
                helperText: error?.message,
                fullWidth,
              },
            }}
          />
          <DateTimePicker
            label={endLabel as any}
            value={end}
            onChange={setEnd}
            ampm={ampm}
            format={fmt}
            disabled={disabled}
            minDateTime={start ?? undefined}
            minutesStep={minutesStep}
            slotProps={{
              textField: {
                size,
                error: !!error,
                helperText: error?.message,
                fullWidth,
              },
            }}
          />
        </>
      )}
    </Box>
  );
}

// TIMEPICKER
type RHFTimePickerProps<TValues extends FieldValues> = {
  control: Control<TValues, any>;
  name: FieldPath<TValues>;
  label?: React.ReactNode;
  rules?: RegisterOptions<TValues, FieldPath<TValues>>;
  serialize?: "string" | "dayjs"; // come salvo nel form
  format?: string; // usato se serialize="string"
  size?: "small" | "medium";
  minutesStep?: number;
  disabled?: boolean;
  fullWidth?: boolean;
};

export function RHFTimePicker<TValues extends FieldValues>({
  control,
  name,
  label,
  rules,
  serialize = "string",
  format = "HH:mm",
  size = "small",
  minutesStep = 5,
  disabled,
  fullWidth = false,
}: RHFTimePickerProps<TValues>) {
  const {
    field,
    fieldState: { error },
  } = useController<TValues>({ name, control, rules });

  // Il picker lavora con Dayjs; se nel form salva stringhe, qui le riconverto
  const viewValue: Dayjs | null =
    typeof field.value === "string"
      ? field.value
        ? dayjs(field.value, format, true)
        : null
      : (field.value ?? null);

  const handleChange = (d: Dayjs | null) => {
    if (serialize === "dayjs") field.onChange(d);
    else field.onChange(d ? d.format(format) : null);
  };

  return (
    <TimePicker
      label={label as any}
      value={viewValue}
      onChange={handleChange}
      ampm={false}
      minutesStep={minutesStep}
      disabled={disabled}
      slotProps={{
        textField: {
          size,
          error: !!error,
          helperText: error?.message,
          fullWidth,
        },
      }}
    />
  );
}
